import { Editor, embeds, type EditorProps } from '@repo/secoda-editor';
import type Extension from '@repo/secoda-editor/lib/Extension';
import Highlight from '@repo/secoda-editor/marks/Highlight';
import Italic from '@repo/secoda-editor/marks/Italic';
import Link from '@repo/secoda-editor/marks/Link';
import type Mark from '@repo/secoda-editor/marks/Mark';
import TemplatePlaceholder from '@repo/secoda-editor/marks/Placeholder';
import Strikethrough from '@repo/secoda-editor/marks/Strikethrough';
import Underline from '@repo/secoda-editor/marks/Underline';
import Attachment from '@repo/secoda-editor/nodes/Attachment';
import Blockquote from '@repo/secoda-editor/nodes/Blockquote';
import BulletList from '@repo/secoda-editor/nodes/BulletList';
import CheckboxItem from '@repo/secoda-editor/nodes/CheckboxItem';
import CheckboxList from '@repo/secoda-editor/nodes/CheckboxList';
import CodeBlock from '@repo/secoda-editor/nodes/CodeBlock';
import CodeFence from '@repo/secoda-editor/nodes/CodeFence';
import Embed from '@repo/secoda-editor/nodes/Embed';
import Emoji from '@repo/secoda-editor/nodes/Emoji';
import HardBreak from '@repo/secoda-editor/nodes/HardBreak';
import Heading from '@repo/secoda-editor/nodes/Heading';
import HorizontalRule from '@repo/secoda-editor/nodes/HorizontalRule';
import Image from '@repo/secoda-editor/nodes/Image';
import ListItem from '@repo/secoda-editor/nodes/ListItem';
import Notice from '@repo/secoda-editor/nodes/Notice';
import OrderedList from '@repo/secoda-editor/nodes/OrderedList';
import { memo, useCallback, useMemo, useState } from 'react';
// Nodes
import type Node from '@repo/secoda-editor/nodes/Node';
import Table from '@repo/secoda-editor/nodes/Table';
import TableCell from '@repo/secoda-editor/nodes/TableCell';
import TableHeadCell from '@repo/secoda-editor/nodes/TableHeadCell';
import TableRow from '@repo/secoda-editor/nodes/TableRow';
// Plugins
import Folding from '@repo/secoda-editor/plugins/Folding';
import History from '@repo/secoda-editor/plugins/History';
import MaxLength from '@repo/secoda-editor/plugins/MaxLength';
import PasteHandler from '@repo/secoda-editor/plugins/PasteHandler';
import Placeholder from '@repo/secoda-editor/plugins/Placeholder';
import SmartText from '@repo/secoda-editor/plugins/SmartText';
import TrailingNode from '@repo/secoda-editor/plugins/TrailingNode';

import { showNotification } from '@mantine/notifications';
import Bold from '@repo/secoda-editor/marks/Bold';
import Comment from '@repo/secoda-editor/marks/Comment';
import DataDiff from '@repo/secoda-editor/marks/DataDiff';
import AISummary from '@repo/secoda-editor/nodes/AISummary';
import TableOfContents from '@repo/secoda-editor/nodes/TableOfContents';
import BlockMenuTrigger from '@repo/secoda-editor/plugins/BlockMenuTrigger';
import EmojiTrigger from '@repo/secoda-editor/plugins/EmojiTrigger';
import FindAndReplaceTrigger from '@repo/secoda-editor/plugins/FindAndReplaceTrigger';
import Hash from '@repo/secoda-editor/plugins/HashPlugin';
import Keys from '@repo/secoda-editor/plugins/Keys';
import ScrollToHeading from '@repo/secoda-editor/plugins/ScrollToHeading';
import SpellCheck from '@repo/secoda-editor/plugins/SpellCheck';
import { useDebouncedCallbackWithFlush } from '../../hooks/useDebouncedCallbackWithFlush';
import { useEditorClickLink } from '../../utils/hook/useEditorClickLink';
import { debounceLocalDefinitionSnapshot } from '../Documentation/utils';
import MentionTrigger from '../Editor/outline/src/extensions/MentionTrigger';
import AiChart from '../Editor/outline/src/nodes/AiChart';
import ChartBlock from '../Editor/outline/src/nodes/ChartBlock';
import Page from '../Editor/outline/src/nodes/Page';
import QueryBlock from '../Editor/outline/src/nodes/QueryBlock';
import ResourceLink from '../Editor/outline/src/nodes/ResourceLink';
import { uploadFile } from '../RichEditor/RichEditor.utils';

export type DocumentationEditorProps = Pick<
	EditorProps,
	| 'id'
	| 'value'
	| 'readOnly'
	| 'disableExtensions'
	| 'template'
	| 'defaultValue'
	| 'editorRef'
	| 'focusedCommentID'
	| 'onCreatePlaceholderComment'
	| 'onClickComment'
	| 'uploadFile'
	| 'onFileUploadStart'
	| 'onFileUploadStop'
	| 'dataTestId'
> & {
	onChangeCallbackDebounceWait?: number;
	disableChangeCallbackDebounce?: boolean;
	onChangeCallback?: (value?: string, saveRemotely?: boolean) => void;
	enableScrollToHash?: boolean;
	emptyLinePlaceholder?: string;
	emptyDocPlaceholder?: string;
};

export const DocumentationEditor = memo(
	({
		onChangeCallback,
		onChangeCallbackDebounceWait = 150,
		disableChangeCallbackDebounce,
		id,
		enableScrollToHash,
		emptyLinePlaceholder = 'Write documentation or press "/" for actions...',
		emptyDocPlaceholder,
		...props
	}: DocumentationEditorProps) => {
		const extensions = useMemo(() => {
			const allExtensions = [
				...[
					new Emoji(),
					new Bold(),
					new Italic(),
					new Underline(),
					new Link(),
					new Strikethrough(),
					new History(),
					new TrailingNode(),
					new Placeholder({
						placeholder: emptyDocPlaceholder,
					}),
					new MaxLength(),
					new Image(),
					new HardBreak(),
					new CodeBlock(),
					new CodeFence(),
					new Blockquote(),
					new Embed(),
					new Attachment(),
					new Notice(),
					new Heading(),
					new HorizontalRule(),
					new Highlight(),
					new DataDiff(),
					new TemplatePlaceholder(),
					new Page(),
					new QueryBlock(),
					new ChartBlock(),
					new ResourceLink(),
					new AISummary(),
					new TableOfContents(),
					new AiChart(),

					new CheckboxList(),
					new CheckboxItem(),
					new BulletList(),
					new OrderedList(),
					new ListItem(),

					// the order matters here - we want Table to be last so that grip-table events are captured last and not compromise the grip-row / grip-column events defined in TableCell and TableHeadCell
					new TableCell(),
					new TableHeadCell(),
					new TableRow(),
					new Table(),
					// -

					new Comment({
						onCreatePlaceholderComment: props.onCreatePlaceholderComment,
						onClickComment: props.onClickComment,
					}),
					new Folding(),
					new SmartText(),
					new PasteHandler(),
					new SpellCheck(),
					new ScrollToHeading(),
					new Hash(),
					new Keys(),
					new FindAndReplaceTrigger(),
					new BlockMenuTrigger({
						emptyLinePlaceholder,
						emptyDocPlaceholder,
					}),
					new MentionTrigger({
						showMentionMenuButton: false, // not in use
					}),
					new EmojiTrigger(),
				],
			] as (Node | Mark | Extension)[];

			return allExtensions;
		}, [
			emptyLinePlaceholder,
			emptyDocPlaceholder,
			props.onClickComment,
			props.onCreatePlaceholderComment,
		]);

		const [scrollTo] = useState(enableScrollToHash ? location.hash : undefined);

		let debounceChangeWait = onChangeCallback
			? onChangeCallbackDebounceWait
			: 4000;
		if (disableChangeCallbackDebounce) {
			debounceChangeWait = 0;
		}

		const handleChange = useDebouncedCallbackWithFlush(
			(value: string | undefined) => {
				if (id) {
					debounceLocalDefinitionSnapshot(id, value);
				}

				if (onChangeCallback) {
					onChangeCallback(value);
				}
			},
			debounceChangeWait
		);

		const onShowToast = useCallback(
			(message: string) =>
				showNotification({
					message: `${message}`,
				}),
			[]
		);

		const onClickLink = useEditorClickLink();

		return (
			<Editor
				{...props}
				extensions={extensions}
				embeds={embeds}
				handleChange={handleChange}
				scrollTo={scrollTo}
				onShowToast={onShowToast}
				onClickLink={onClickLink}
				uploadFile={uploadFile}
			/>
		);
	}
);
